import { RentalDuration, RentalStatus } from './rental';
import { User } from './user';

export enum EquipmentStateOfUse {
  NEW = 1,
  USED = 2,
}

export enum EquipmentType {
  SURF = 1,
  PADDLE = 2,
  KAYAK = 3,
  BIKE = 4,
}

export interface EquipmentPrice {
  id: number;
  price: number;
  rentalDuration: RentalDuration;
}

export interface Equipment {
  id: number;
  model: string;
  description: string;
  equipmentPrices: EquipmentPrice[];
  deposit: number;
  imageUrl: string;
  stateOfUse: EquipmentStateOfUse;
  rentalStatus: RentalStatus;
  type: EquipmentType;
  createdAt: string;
  updatedAt: string;
  lockId: number;
  lock?: Lock;
}

export enum LockHook {
  OPENED = 1,
  CLOSED = 0,
}

export interface Lock {
  id: number;
  hook: LockHook;
  active: boolean;
  ecuId: number;
  position: number;
  createdAt: string;
  updatedAt: string;
  equipments?: Equipment[];
  ecu?: Ecu;
}

export interface Bus {
  id: number;
  output: number;
  diagnostic: number;
}

export interface MainLock {
  id: number;
  hook: LockHook;
  output: number;
  diagnostic: number;
}

export interface Ecu {
  id: number;
  address: number;
  isPrincipal: boolean;
  maxLocks: number;
  firmwareVersion?: string;
  locks: Lock[];
  bus?: Bus;
  mainLock?: MainLock;
  station?: Station;
}

export interface ReliabilityMetrics {
  boot?: number;
  crash?: number;
  mqtt_r?: number;
  pdp_r?: number;
  full_r?: number;
  hard_r?: number;
  up?: number;
  rl?: number;
}

export interface Station {
  id: number;
  name?: string;
  lat: number;
  lng: number;
  token: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  locationId: number;
  voltage?: number;
  ecus: Ecu[];
  rentalDurations: RentalDuration[];
  firmwareVersion?: string;
  networkOperator?: string | null;
  rssi?: number | null;
  rsrp?: number | null;
  sinr?: number | null;
  rsrq?: number | null;
  signalUpdatedAt?: string | null;
  reliabilityMetrics?: ReliabilityMetrics | null;
  discountPercentage?: number;
}

export interface SyncLogEntry {
  id: number;
  stationId: number;
  voltage: number | null;
  rssi: number | null;
  rsrp: number | null;
  sinr: number | null;
  rsrq: number | null;
  networkOperator: string | null;
  firmwareVersion: string | null;
  reliabilityMetrics: ReliabilityMetrics | null;
  createdAt: string;
}

export interface ConnectivityWindow {
  downFrom: string;
  downTo: string | null;
  durationMinutes: number;
  likelyReboot: boolean;
  ongoing: boolean;
}

export interface StationConnectivity {
  windows: ConnectivityWindow[];
  sampleCount: number;
  rebootCount: number;
}

export interface StationHealth {
  stationId: number;
  token: string;
  locationId: number | null;
  locationName: string | null;
  status: 'online' | 'offline' | 'inactive';
  active: boolean;
  lastSeen: string | null;
  batteryVoltage?: number | null;
  firmwareVersion?: string | null;
  signal: {
    rssi?: number | null;
    rsrp?: number | null;
    sinr?: number | null;
    rsrq?: number | null;
    updatedAt?: string | null;
  };
  lockCount: number;
  activeLockCount: number;
  reliabilityMetrics?: ReliabilityMetrics | null;
}

export enum Day {
  MONDAY = 0,
  TUESDAY = 1,
  WEDNESDAY = 2,
  THURSDAY = 3,
  FRIDAY = 4,
  SATURDAY = 5,
  SUNDAY = 6,
}

export interface LocationSchedule {
  locationId: number;
  day: Day;
  startTime: string;
  endTime: string;
}

export interface Location {
  id: number;
  name: string;
  description?: string;
  addressStreet: string;
  addressStreet2?: string;
  addressPostalCode: string;
  addressCity: string;
  addressCountry: string;
  lat: number;
  lng: number;
  bannerUrl?: string;
  mapUrl?: string;
  termsUrl?: string;
  websiteUrl?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  stations: Station[];
  schedules: LocationSchedule[];
  user?: User;
}
